import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus, X, GripHorizontal, Palette } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from '../../api/apiClient';

// API
const adminApi = {
    getRules: apiClient.admin.getColorRules,
    getUnmapped: apiClient.admin.getUnmappedColors,
    createRule: apiClient.admin.createColorRule,
    updateRule: ({ id, data }) => apiClient.admin.updateColorRule(id, data),
    deleteRule: apiClient.admin.deleteColorRule,
    seedRules: apiClient.admin.seedColorRules
};

import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowRight, Search } from "lucide-react";

export default function AdminColors() {
    const queryClient = useQueryClient();
    const [newColorName, setNewColorName] = useState('');
    const [newHexCode, setNewHexCode] = useState('#000000');
    const [searchTerm, setSearchTerm] = useState('');

    // Data
    const { data: rules = [] } = useQuery({ queryKey: ['colorRules'], queryFn: adminApi.getRules, staleTime: 0 });
    const { data: unmapped = [] } = useQuery({ queryKey: ['unmappedColors', rules], queryFn: adminApi.getUnmapped });

    // Mutations
    const createMutation = useMutation({
        mutationFn: adminApi.createRule,
        onSuccess: () => {
            queryClient.invalidateQueries(['colorRules']);
            setNewColorName('');
        }
    });

    const updateMutation = useMutation({
        mutationFn: adminApi.updateRule,
        onSuccess: () => queryClient.invalidateQueries(['colorRules'])
    });

    const deleteMutation = useMutation({
        mutationFn: adminApi.deleteRule,
        onSuccess: () => queryClient.invalidateQueries(['colorRules'])
    });

    const seedMutation = useMutation({
        mutationFn: adminApi.seedRules,
        onSuccess: () => queryClient.invalidateQueries(['colorRules'])
    });

    // Handlers
    const handleCreateColor = (name = newColorName, hex = newHexCode, keywords = null) => {
        if (!name.trim()) return;
        createMutation.mutate({
            name: name.toLowerCase(), // Canonical is lowercase
            hex_code: hex,
            keywords: keywords || [name.toLowerCase()],
            priority: rules.length * 10 + 10
        });
    };

    const handleAddKeyword = (rule, keyword) => {
        if (!keyword.trim()) return;
        if (rule.keywords.includes(keyword)) return;

        updateMutation.mutate({
            id: rule.id,
            data: { ...rule, keywords: [...rule.keywords, keyword] }
        });
    };

    const handleRemoveKeyword = (rule, keywordToRemove) => {
        updateMutation.mutate({
            id: rule.id,
            data: { ...rule, keywords: rule.keywords.filter(k => k !== keywordToRemove) }
        });
    };

    const handleDeleteColor = (id) => {
        if (confirm("Supprimer cette couleur ?")) {
            deleteMutation.mutate(id);
        }
    };

    const handleSeedDefaults = () => {
        if (confirm("Voulez-vous restaurer les couleurs par défaut ?")) {
            seedMutation.mutate();
        }
    };

    const filteredRules = rules.filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div className="flex flex-col md:flex-row h-screen md:h-[calc(100vh-65px)] overflow-hidden bg-gray-50">
            {/* Main Content */}
            <div className="flex-1 p-8 overflow-auto">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-3xl font-bold">Configuration des Couleurs</h1>
                        <p className="text-gray-500">Gérez les couleurs et leurs synonymes pour les filtres.</p>
                    </div>

                    <div className="flex gap-2 items-center bg-white p-2 rounded-lg shadow-sm border">
                        <div className="flex items-center gap-2">
                            <Search className="w-4 h-4 text-gray-400 ml-2" />
                            <Input
                                placeholder="Filtrer..."
                                className="w-24 border-0 shadow-none focus-visible:ring-0 mr-2"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                            <div className="relative">
                                <input
                                    type="color"
                                    value={newHexCode}
                                    onChange={e => setNewHexCode(e.target.value)}
                                    className="w-8 h-8 rounded cursor-pointer border-0 p-0"
                                    title="Code couleur"
                                />
                            </div>
                            <Input
                                placeholder="Nouvelle couleur..."
                                className="w-40 border-0 shadow-none focus-visible:ring-0"
                                value={newColorName}
                                onChange={e => setNewColorName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleCreateColor()}
                            />
                        </div>
                        <Button onClick={() => handleCreateColor()} size="sm">
                            <Plus className="mr-2 h-4 w-4" /> Créer
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 content-start pb-20">
                    {rules.length === 0 && (
                        <div className="col-span-full flex flex-col items-center justify-center p-12 text-gray-400 border-2 border-dashed rounded-lg bg-white">
                            <Palette className="h-12 w-12 mb-4 opacity-20" />
                            <p>Aucune couleur configurée.</p>
                            <Button variant="outline" onClick={handleSeedDefaults} className="mt-4">
                                Restaurer les couleurs par défaut
                            </Button>
                        </div>
                    )}

                    {filteredRules.map(rule => (
                        <ColorCard
                            key={rule.id}
                            rule={rule}
                            onAddKeyword={handleAddKeyword}
                            onRemoveKeyword={handleRemoveKeyword}
                            onDelete={() => handleDeleteColor(rule.id)}
                            onUpdate={(data) => updateMutation.mutate({ id: rule.id, data })}
                        />
                    ))}
                </div>
            </div>

            {/* Sidebar - Unmapped Colors */}
            <div className="w-full md:w-80 bg-white border-l shadow-xl flex flex-col z-10 h-64 md:h-auto">
                <div className="p-4 border-b bg-gray-50/50">
                    <h2 className="font-semibold flex items-center gap-2">
                        <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-bold">{unmapped.length}</span>
                        Couleurs Non Classées
                    </h2>
                    <p className="text-xs text-gray-500 mt-1">
                        Couleurs détectées non liées.
                    </p>
                </div>

                <ScrollArea className="flex-1 p-4">
                    <div className="space-y-2">
                        {unmapped.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">Tout est bien rangé ! 🎉</p>
                        ) : unmapped.map(color => (
                            <div key={color} className="group flex items-center justify-between p-2 rounded-md border border-gray-100 hover:border-amber-200 hover:bg-amber-50 transition-all bg-white text-sm">
                                <span className="truncate flex-1 font-medium text-gray-700 capitalize">{color}</span>

                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {/* Promote to new Color */}
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-6 w-6 text-amber-600 hover:text-amber-700 hover:bg-amber-100"
                                        title="Créer une couleur"
                                        onClick={() => handleCreateColor(color, '#000000', [color])}
                                    >
                                        <Plus className="h-3 w-3" />
                                    </Button>

                                    {/* Add to existing Color */}
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                className="h-6 w-6 text-amber-600 hover:text-amber-700 hover:bg-amber-100"
                                                title="Ajouter comme synonyme"
                                            >
                                                <ArrowRight className="h-3 w-3" />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="p-2 w-48 max-h-64 overflow-auto" align="end">
                                            <div className="space-y-1">
                                                <p className="text-xs font-semibold text-gray-500 mb-2 px-1">Lier à...</p>
                                                <Input
                                                    placeholder="Rechercher..."
                                                    className="h-7 text-xs mb-2"
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) => {
                                                        // Simple filter in visual list if needed, or rely on scroll
                                                    }}
                                                />
                                                {rules.map(rule => (
                                                    <button
                                                        key={rule.id}
                                                        className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-100 focus:bg-gray-100 flex items-center gap-2"
                                                        onClick={() => handleAddKeyword(rule, color)}
                                                    >
                                                        <span className="w-3 h-3 rounded-full border" style={{ background: rule.hex_code }}></span>
                                                        {rule.name}
                                                    </button>
                                                ))}
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>
                        ))}
                    </div>
                </ScrollArea>
            </div>
        </div>
    );
}

function ColorCard({ rule, onAddKeyword, onRemoveKeyword, onDelete, onUpdate }) {
    const [input, setInput] = useState('');
    const [isEditingHex, setIsEditingHex] = useState(false);
    const [hexInput, setHexInput] = useState(rule.hex_code);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (input.trim()) {
                onAddKeyword(rule, input.trim());
                setInput('');
            }
        }
    };

    const handleHexSave = () => {
        if (hexInput !== rule.hex_code) {
            onUpdate({ ...rule, hex_code: hexInput });
        }
        setIsEditingHex(false);
    };

    return (
        <Card className="flex flex-col h-full shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                <div className="flex items-center gap-3">
                    <div
                        className="w-8 h-8 rounded-full border shadow-sm cursor-pointer hover:scale-110 transition-transform relative group"
                        style={{ background: rule.hex_code }}
                        onClick={() => setIsEditingHex(true)}
                        title="Modifier la couleur"
                    >
                        {isEditingHex && (
                            <input
                                type="color"
                                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                value={hexInput}
                                onChange={e => {
                                    setHexInput(e.target.value);
                                    // Auto save on color pick? Maybe better on blur or distinct action.
                                    // Let's rely on onBlur or manual close if possible.
                                    // Input type color closes on selection often.
                                }}
                                onBlur={handleHexSave}
                            />
                        )}
                    </div>
                    <div>
                        <CardTitle className="text-lg font-bold capitalize">
                            {rule.name}
                        </CardTitle>
                        <CardDescription className="text-xs font-mono mt-0.5">
                            {rule.hex_code}
                        </CardDescription>
                    </div>
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-red-500" onClick={onDelete}>
                    <Trash2 className="h-3 w-3" />
                </Button>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col gap-3">
                <div className="flex flex-wrap gap-1.5 min-h-[50px] content-start">
                    {rule.keywords.map(keyword => (
                        <Badge key={keyword} variant="secondary" className="pr-1 hover:bg-gray-100 transition-colors group cursor-default border-gray-100">
                            {keyword}
                            <button
                                onClick={() => onRemoveKeyword(rule, keyword)}
                                className="ml-1 rounded-full p-0.5 hover:bg-red-200 hover:text-red-700 opacity-50 group-hover:opacity-100 transition-all"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    ))}
                </div>

                <div className="mt-auto pt-2 relative">
                    <Input
                        className="h-8 text-xs pr-8"
                        placeholder="+ Synonyme..."
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    {input && (
                        <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 absolute right-1 top-3 text-amber-600"
                            onClick={() => { onAddKeyword(rule, input); setInput(''); }}
                        >
                            <Plus className="h-3 w-3" />
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
